import * as React from "react";

import { cn } from "@/lib/utils";

/**
 * A group of mutually exclusive options, as a segmented control.
 *
 * The dashboard renders these as several Bootstrap `custom-switch` checkboxes
 * side by side, each one written so that unchecking re-selects itself: three
 * on/off switches impersonating one radio group. That reads as "any number of
 * these can be on" to both the eye and a screen reader, and it disappears with
 * Bootstrap.
 *
 * Built on native radios — visually hidden, not replaced — so arrow-key
 * navigation, focus and form semantics come from the browser.
 */
const SegmentedControl = ({ name, label, options, value, onChange, className }) => (
  <fieldset className={cn("min-w-0 border-0 p-0", className)}>
    {label && (
      <legend className="mb-1.5 text-[13px] text-muted-foreground">
        {label}
      </legend>
    )}

    <div className="inline-flex items-center gap-1 rounded-full border border-border bg-muted p-1">
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <label
            key={option.value}
            className={cn(
              "mb-0 flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-full px-3 py-1.5",
              "text-xs font-semibold transition-colors duration-150",
              "has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring",
              isSelected
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <input
              type="radio"
              name={name}
              value={option.value}
              checked={isSelected}
              onChange={() => onChange(option.value)}
              className="sr-only"
            />
            {option.label}
          </label>
        );
      })}
    </div>
  </fieldset>
);

export { SegmentedControl };
